import React from "react";
// ANT DESIGN COMPONENTS
import { Dropdown, Menu, Space, message } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faFileExport,
  faTrash,
  faPenToSquare,
  faBan,
} from "@fortawesome/free-solid-svg-icons";
// NAVIGATION
import { useNavigate } from "react-router-dom";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { updateOpenModalDeleteLift, updateLift } from "../../../redux/lifts/liftSlice";
// WIZARD NAVIGATION
import { WizardStep } from "../../setupWizard/utils/wizardInvalidation";
// AUTHORIZATION
import { Permission, hasPermission } from "../../../utils/validatePermissions";
// COMPONENTS
import TableActionsMenuItem from "../../components/table/TableActionsMenuItem";
// INTERFACES
import { LiftInterface } from "../../../interfaces/Lift.interface";
// INTERFACES
import { RootState } from "../../../app/store";
// AUTH
import downloadEntityAsBinary from "../../../utils/exportEntity";
import { formatUuidT } from "../../../flatbuffers/display";

function LiftsTableActions({ selectedItem }: { selectedItem: LiftInterface }) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { permissionSet } = useSelector((state: RootState) => state.user);
  const { globalOrganizationId } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const handleOpenModalEdit = () => {
    const orgId = formatUuidT(selectedItem.organizationId) || "";
    if (orgId) {
      navigate(`/organization-management/setup-wizard?orgId=${orgId}&step=${WizardStep.Lifts}`);
    }
  };

  const handleOpenModalDelete = () => {
    dispatch(updateLift(selectedItem));
    dispatch(updateOpenModalDeleteLift(true));
  };

  const handleExportLiftBinary = async () => {
    if (!globalOrganizationId) {
      message.error("No organization selected.");
      return;
    }
    try {
      await downloadEntityAsBinary(
        globalOrganizationId,
        "lifts",
        typeof selectedItem.name === "string" ? selectedItem.name : undefined,
      );
    } catch {
      message.error("Failed to export lift.");
    }
  };

  // ************************************************ */
  // USE EFFECT ************************************* */

  // ************************************************ */
  // ROW ACTIONS ************************************ */
  const manageActions = () => {
    const BLANK = {
      label: (
        <div className="generalStyles__tableActionBox">
          <span>
            <FontAwesomeIcon className="generalStyles__error generalStyles__mrFix" icon={faBan} />
            {t("general.noActions")}
          </span>
        </div>
      ),
      key: "0",
    };

    const EDIT = hasPermission(Permission.LiftsUpdate, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faPenToSquare}
              text={t("organizationManagement.edit")}
              onClick={handleOpenModalEdit}
            />
          ),
          key: "2",
        }
      : null;

    const DELETE = hasPermission(Permission.LiftsDelete, permissionSet ?? [])
      ? {
          label: (
            <TableActionsMenuItem
              icon={faTrash}
              text={t("organizationManagement.delete")}
              onClick={handleOpenModalDelete}
            />
          ),
          key: "3",
        }
      : null;

    const EXPORT = hasPermission(Permission.LiftsView, permissionSet ?? [])
      ? {
          label: (
            <span>
              <FontAwesomeIcon
                className="generalStyles__info generalStyles__mrFix"
                icon={faFileExport}
              />
              {t("organizationManagement.exportDescription")}
            </span>
          ),
          key: "4",
          onClick: handleExportLiftBinary,
        }
      : null;

    // RETURN OBJECT WITH VALID ACTIONS
    if (EDIT || DELETE) {
      return [EDIT, EXPORT, DELETE];
    }
    return [BLANK];
  };

  const menu = <Menu items={manageActions()} />;

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <Dropdown dropdownRender={() => menu} trigger={["click"]}>
        <Space className="generalStyles__hoverCursor">
          <FontAwesomeIcon icon={faEllipsis} />
        </Space>
      </Dropdown>
    </div>
  );
}

export default LiftsTableActions;
